import includes from 'lodash/includes';
import isPlainObject from 'lodash/isPlainObject';
import throttle from 'lodash/throttle';

import BetType from 'atg/api/BetType';
import {
  CURRENT_INPUT_CHANGE,
  GRID_ON_OVERLAY_PRESSED,
} from 'atg/constants/Events';
import observeStore from 'atg/redux/observeStore';
import { isTouch } from 'atg/utils/device';
import EventDispatcher from 'atg/utils/EventDispatcher';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { ui } from 'shared/redux/selectors';
import { getHorses } from 'shared/redux/selectors/monitorSelectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

import { createHelpIcon } from '../HelpIcon';
import GridCellInteractionManager from './GridCellInteractionManager';

const $ = window.$;

const getCellPosition = $cell => {
  const jqCell = $cell instanceof $ ? $cell : $($cell);

  const $row = jqCell.closest('.row');
  const position = {};

  position.row = $row.index();
  const column = jqCell.index();

  if (column > 0) {
    position.column = column - 1;
  }

  return position;
};

const getHorseInfoHelpIcon = (header, body) =>
  createHelpIcon({
    header: Text.get(header),
    body: Text.get(body),
  });

export default class Grid {
  constructor(delegate, $scrollContainer, $parent) {
    this.delegate = delegate;
    this.api = Utils.callDelegate(delegate, 'getViewAPI');
    this.$scrollContainer = $scrollContainer;

    this.onResize = this.onResize.bind(this);
    this.onFirstCellPressed = this.onFirstCellPressed.bind(this);
    this.onFirstCellHover = this.onFirstCellHover.bind(this);
    this.onCellPressed = this.onCellPressed.bind(this);
    this.onOverlayPressed = this.onOverlayPressed.bind(this);
    this.onInputFocus = this.onInputFocus.bind(this);
    this.onNumberInputChanged = this.onNumberInputChanged.bind(this);
    this.onNumberInputKeyDown = this.onNumberInputKeyDown.bind(this);
    this.onLetterInputChanged = this.onLetterInputChanged.bind(this);
    this.onLetterInputKeyDown = this.onLetterInputKeyDown.bind(this);
    this.onUiScaleChange = this.onUiScaleChange.bind(this);
    this.handleCellButtonsDocumentClick =
      this.handleCellButtonsDocumentClick.bind(this);
    this.onCurrentInputDeviceChange =
      this.onCurrentInputDeviceChange.bind(this);

    this.$firstCells = $();

    const $container = (this.$container = StickyTemplate.jq('grid'));
    $parent.append($container);

    this.$header = $container.find('.header');
    this.$rows = $container.find('.rows');
    this.$overlay = $container.find('.overlay');
    this.$cellButtons = $container.find('.cell-buttons');

    this._gridCellInteractionManager = new GridCellInteractionManager({
      grid: this,
      getCellPosition,
      $scrollContainer: this.$scrollContainer,
      onTap: ($cell, event) => {
        Utils.callDelegate(
          this.delegate,
          'onGridCellTapped',
          getCellPosition($cell),
          event,
        );
      },
      onDoubleTap: ($cell, event) => {
        Utils.callDelegate(
          this.delegate,
          'onGridCellDoubleTapped',
          getCellPosition($cell),
          event,
        );
      },
      onSort: (row, indexMap) => {
        Utils.callDelegate(
          this.delegate,
          'onGridRowOrderChanged',
          row,
          indexMap,
        );
      },
    });

    this.$overlay.event('click', this.onOverlayPressed);

    const throttledResize = throttle(this.onResize, 100);
    $(window).event('resize', throttledResize);

    this.destroyStoreScaleObserver = observeStore(
      getStore(),
      this.onUiScaleChange,
      ui.getScale,
    );
    this.destroyStoreMyHorsesObserver = observeStore(
      getStore(),
      () => this.updateAll(),
      getHorses,
    );
    EventDispatcher.addListener(
      CURRENT_INPUT_CHANGE,
      this.onCurrentInputDeviceChange,
    );
  }

  destroy() {
    this.destroyStoreScaleObserver();
    this.destroyStoreMyHorsesObserver();
    EventDispatcher.removeListener(
      CURRENT_INPUT_CHANGE,
      this.onCurrentInputDeviceChange,
    );
    this._gridCellInteractionManager.destroy();
  }

  onUiScaleChange() {
    if (this.$overlay.hasClass('active')) {
      this.closeHighlight();
    }
  }

  onCurrentInputDeviceChange() {
    // eslint-disable-next-line unicorn/no-this-assignment
    const self = this;
    this.$rows.find('.row').each(function () {
      $(this)
        .find('.cell')
        .each(function (index) {
          if (index > 0) {
            // Rebind event listener so we get the correct event type
            // for the current input (touch or mouse)
            $(this).unevent('down', self.onCellPressed);
            $(this).event('down', self.onCellPressed);
          }
        });
    });
  }

  setSize(columns, rows) {
    let numColumns = columns;
    let numRows = rows;

    if (isPlainObject(columns) && columns.columns && columns.rows) {
      numRows = columns.rows;
      numColumns = columns.columns;
    }

    this.columns = numColumns;
    this.rows = numRows;

    this.$firstCells = $();

    const $headerCells = this.$header.find('.cell');
    let column;

    for (column = 0; column < numColumns + 1; column++) {
      let $cell;

      if (column >= $headerCells.length) {
        $cell =
          column === 0
            ? StickyTemplate.jq('grid-header-first-cell')
            : StickyTemplate.jq('grid-header-cell');
        this.$header.append($cell);
      } else {
        $cell = $headerCells.eq(column);
      }

      if (column === 0) {
        this.$firstCells = this.$firstCells.add($cell);
      }
    }

    for (; column < $headerCells.length; column++) {
      const $cell = $headerCells.eq(column);
      $cell.remove();
    }

    const $rows = this.$rows.find('.row');

    let row;

    for (row = 0; row < numRows; row++) {
      let $row;
      let $cell;
      if (row >= $rows.length) {
        $row = StickyTemplate.jq('grid-row');
        this.$rows.append($row);
      } else {
        $row = $rows.eq(row);
      }

      const $rowCells = $row.find('.cell');

      for (column = 0; column < numColumns + 1; column++) {
        if (column >= $rowCells.length) {
          if (column === 0) {
            $cell = StickyTemplate.jq('grid-first-cell');
            $cell.event('click', this.onFirstCellPressed);
          } else {
            $cell = StickyTemplate.jq('grid-cell');
            $cell.event('down', this.onCellPressed);
          }
          $row.append($cell);
        } else {
          $cell = $rowCells.eq(column);
        }

        if (column === 0) {
          this.$firstCells = this.$firstCells.add($cell);
        }
      }

      for (; column < $rowCells.length; column++) {
        $cell = $rowCells.eq(column);
        $cell.remove();
      }
    }

    for (; row < $rows.length; row++) {
      const $row = $rows.eq(row);
      $row.remove();
    }

    this.updateOverlaySize();
  }

  getSize() {
    return {
      columns: this.columns,
      rows: this.rows,
    };
  }

  getCellByPosition(position) {
    return this.$container.find(
      `.row:eq(${position.row}) .cell:eq(${Number.parseInt(
        position.column + 1,
        10,
      )})`,
    );
  }

  setClass(className) {
    this.$container.removeClassPrefix('view-');
    this.$container.addClass(className);
  }

  // ---------------------------------------- ITEMS ----------------------------------------

  updateAll() {
    const cls = Utils.callDelegate(this.delegate, 'getGridClass');

    this.setClass(cls);

    const columns = Utils.callDelegate(
      this.delegate,
      'getGridNumberOfGridColumns',
    );
    const rows = Utils.callDelegate(this.delegate, 'getGridNumberOfGridRows');

    this.setSize(columns, rows);

    this.updateHeaders();

    this.createCopyButtonForSingleRace();

    for (let row = 0; row < rows; row++) {
      this.updateRow(row);
    }
  }

  createCopyButtonForSingleRace() {
    const $rows = this.$rows.find('.row');
    if ($rows.length === 0) {
      return; // No parent row to attach button to yet
    }
    if (this.$copyButton != null) {
      return; // Button already created, don't recreate
    }

    // Single Race bet types need a copy button at the end of the first row
    // to allow user to copy the first-row order to all subsequent rows
    const betTypeCode = this.api.betInformation.getBetKey()?.getBetType();
    if (BetType.getBetType(betTypeCode)?.hasSingleRace()) {
      const $copyCell = StickyTemplate.jq('grid-copy-cell', {
        '.button-caption': Text.get('grid-row-copy-button-caption'),
      });
      this.$copyButton = $copyCell.find('.copy-button');
      this.$copyButton.event('click', () => this.onSingleRaceCopyRanking());
      this.updateCopyButtonState();
      $rows.first().append($copyCell);
    }
  }

  updateHeaders() {
    const isTrio = this.api.betInformation.getBetKey()?.getBetType() === 'T';
    const helpTexts = {
      'view-ranking': {
        header: 'help-horse-box-header',
        body:
          IS_SE_SITE && isTrio
            ? 'help-horse-box-body-trio'
            : 'help-horse-box-body',
      },
      'view-ABCD-reduction': {
        header: 'help-ABCD-reduction-header',
        body: 'help-ABCD-reduction-body',
      },
      'view-point-reduction': {
        header: 'help-point-reduction-header',
        body: 'help-point-reduction-body',
      },
      'view-base-horse-reduction': {
        header: 'help-base-reduction-header',
        body: 'help-base-reduction-body',
      },
      'view-own-reduction': {
        header: 'help-own-reduction-header',
        body: 'help-own-reduction-body',
      },
      'view-trend': {
        header: 'help-trend-reduction-header',
        body: 'help-trend-reduction-body',
      },
      'view-start-number-reduction': {
        header: 'help-start-number-reduction-header',
        body: 'help-start-number-reduction-body',
      },
      'view-jockey-reduction': {
        header: 'help-jockey-reduction-header',
        body: 'help-jockey-reduction-body',
      },
      'view-trainer-reduction': {
        header: 'help-trainer-reduction-header',
        body: 'help-trainer-reduction-body',
      },
      'view-dividend-reduction': {
        header: 'help-dividend-reduction-header',
        body: 'help-dividend-reduction-body',
      },
      'view-factor-reduction': {
        header: 'help-factor-reduction-header',
        body: 'help-factor-reduction-body',
      },
      'view-preset-reduction': {
        header: 'preset-reduction-help-header',
        body: 'preset-reduction-help-body',
      },
    };

    const $headerCells = this.$header.find('.cell:not(:first-child)');
    const delegate = this.delegate;

    $headerCells.each(function (value) {
      $(this).text(Utils.callDelegate(delegate, 'getGridHeader', value));
      $(this).reevent(
        'click',
        Utils.callDelegate.bind(
          this,
          delegate,
          'onGridHeaderCellPressed',
          $(this),
        ),
      );
    });

    const upperleftCell = this.$header.find('.cell.first');

    upperleftCell.empty();
    for (const property in helpTexts) {
      if (this.$container.hasClass(property)) {
        upperleftCell.append(
          getHorseInfoHelpIcon(
            helpTexts[property].header,
            helpTexts[property].body,
          ),
        );
      }
    }

    Utils.callDelegate(delegate, 'onHeaderRender', this.$header);
  }

  updateRow(row) {
    if (this._gridCellInteractionManager.isActiveInRow(row)) {
      this._gridCellInteractionManager.setPendingRowUpdate(true);
      return;
    }

    this._gridCellInteractionManager.setPendingRowUpdate(false);

    const columns = Utils.callDelegate(
      this.delegate,
      'getGridNumberOfRowColumns',
      row,
    );

    const $row = this.$rows.find(`.row:eq(${row})`);

    const rowInfo =
      Utils.callDelegate(this.delegate, 'getGridRowInfo', row) || {};
    const numberOfHorses = rowInfo.numberOfHorses || 0;

    $row.removeClassPrefix('race-');
    if (rowInfo.raceNumber) {
      $row.addClass(`race-${Utils.wrapRaceNumber(rowInfo.raceNumber)}`);
    }

    const $cells = $row.children('.cell');

    const $firstCell = $cells.eq(0);

    StickyTemplate.params($firstCell, {
      '.time': rowInfo.time || '',
      '.title': rowInfo.title || '',
      '.info': rowInfo.info || '',
    });

    $firstCell.reevent('mouseenter', this.onFirstCellHover);
    $firstCell.reevent('mouseleave', this.onFirstCellHover);

    const position = { row: row };
    let rowCache;

    for (
      position.column = 0;
      position.column < $cells.length;
      position.column++
    ) {
      const $cell = $cells.eq(position.column + 1);
      const hasItem = position.column < columns;
      const inRange = position.column < numberOfHorses;
      const hasBet = rowInfo?.nrOfBetsRow?.getNrOfBets(position.column) > 0;
      let cellInfo;

      if (hasItem) {
        cellInfo =
          Utils.callDelegate(
            this.delegate,
            'getGridCellInfo',
            position,
            rowCache,
          ) || {};
        rowCache = cellInfo.rowCache;
      } else {
        cellInfo = {};
      }

      StickyTemplate.params($cell, {
        '.number': cellInfo.number || '',
        '.left-info .info-1': cellInfo.leftInfo1 || '',
        '.left-info .info-2': cellInfo.leftInfo2 || '',
        '.right-info .shoe': cellInfo.rightInfo1 || '',
        '.right-info .wagon': cellInfo.rightInfo2 || '',
        '.horse-name': cellInfo.horseName || '',
        '.jockey-trainer-name': cellInfo.jockeyTrainerName || '-',
        '.bottom-info': cellInfo.bottomInfo || '',
        '.bottom-blue': cellInfo.bottomBlue != null ? cellInfo.bottomBlue : '',
      });

      const $jockeyTrainerNameElement = $cell.find('.jockey-trainer-name');
      if (cellInfo.originalJockey) {
        if (!$jockeyTrainerNameElement.hasClass('replacement')) {
          $jockeyTrainerNameElement.addClass('replacement');
          $jockeyTrainerNameElement.reevent(
            'mouseenter mouseleave',
            this.onReplacementJockeyHover.bind(this, cellInfo.originalJockey),
          );
        }
      } else {
        if ($jockeyTrainerNameElement.hasClass('replacement')) {
          $jockeyTrainerNameElement.removeClass('replacement');
          $jockeyTrainerNameElement.off('mouseenter mouseleave');
        }
      }

      $cell.removeClass().addClass('cell item');
      if (cellInfo.cellClass != null) {
        $cell.addClass(cellInfo.cellClass);
      }

      $cell.toggleClass('item', hasItem);
      $cell.toggleClass('in-range', inRange);
      $cell.toggleClass('has-bet', hasBet);
      $cell.toggleClass('winner', cellInfo.winner || false);
      $cell.toggleClass('scratched', cellInfo.scratched || false);

      const $checkbox = $cell.find('.checkbox');
      if (cellInfo.checkbox != null) {
        $checkbox.show();
        $checkbox.find('input').prop('checked', cellInfo.checkbox);
      } else {
        $checkbox.hide();
      }
      const $commentIndicator = $cell.find('.comment-indicator');
      if (cellInfo.commentIndicator) {
        $commentIndicator.show();
      } else {
        $commentIndicator.hide();
      }
    }

    this.updateCopyButtonState();

    Utils.callDelegate(this.delegate, 'onRowWasUpdated', row);
  }

  updateCopyButtonState() {
    // If there is a copyButton, make it disabled or enabled depending on row orders
    if (this.$copyButton != null) {
      const orders = this.api.betInformation
        .getRows()
        .map(row => row.getOrder().join(','));
      if (new Set(orders).size === 1) {
        // One single unique row, i.e. all same, disable button
        this.$copyButton.addClass('disabled');
      } else {
        // More than one unique row, enable button
        this.$copyButton.removeClass('disabled');
      }
    }
  }

  // ---------------------------------------- SCROLLING ----------------------------------------

  getScrollPosition() {
    return {
      x: Math.max(0, this.$scrollContainer.scrollLeft()),
      y: Math.max(0, this.$scrollContainer.scrollTop()),
    };
  }

  // ---------------------------------------- RESIZE ----------------------------------------

  onResize() {
    this.updateOverlaySize();
    this.closeHighlight();
  }

  // ---------------------------------------- EVENTS ----------------------------------------

  onFirstCellPressed(event) {
    const position = getCellPosition(event.currentTarget);
    Utils.callDelegate(
      this.delegate,
      'onGridFirstCellHover',
      position.row,
      false,
    );
    Utils.callDelegate(this.delegate, 'onGridFirstCellPressed', position.row);
  }

  onFirstCellHover(event) {
    const position = getCellPosition(event.currentTarget);
    const showPopup = event.type === 'mouseenter' ? true : false;

    Utils.callDelegate(
      this.delegate,
      'onGridFirstCellHover',
      position.row,
      showPopup,
    );
  }

  onCellPressed(event) {
    // Exit unless touch or left mouse is clicked
    if (!isTouch() && event.type === 'mousedown' && event.which !== 1) {
      return;
    }

    // Exit on multi touch
    if (Utils.getEventTouchCount(event) > 1) {
      return;
    }

    const $cell = $(event.currentTarget);
    const cellPosition = getCellPosition($cell);

    if (Utils.isModifierKeyPressed(event)) {
      Utils.callDelegate(
        this.delegate,
        'onGridInfoButtonPressed',
        cellPosition,
      );
      return;
    }

    this._gridCellInteractionManager.handleCellPress({
      event,
      $cell,
      gridSize: this.getSize(),
      isBlockedFn: this.delegate.isBetBlocked,
      showAlertWhenBlockedFn: this.delegate.showAlertWhenLocked,
    });
  }

  onReplacementJockeyHover(jockey, event) {
    const showPopup = event.type === 'mouseenter' ? true : false;
    const position = {
      top: event.pageY - event.offsetY + event.currentTarget.offsetHeight,
      left: event.pageX - event.offsetX,
    };
    Utils.callDelegate(
      this.delegate,
      'onReplacementJockeyHover',
      jockey,
      position,
      showPopup,
    );
  }

  onSingleRaceCopyRanking() {
    const targetOrder = this.api.betInformation.getRow(0).getOrder();
    for (let i = 1; i < this.api.betInformation.getNumberOfRows(); i++) {
      const originalOrder = this.api.betInformation.getRow(i).getOrder();
      const indexMap = targetOrder.map(t => originalOrder.indexOf(t));
      Utils.callDelegate(this.delegate, 'onGridRowOrderChanged', i, indexMap);
    }
  }

  // ---------------------------------------- POSITIONING ----------------------------------------

  getTop() {
    return this.$container.offset().top;
  }

  getLeft() {
    return this.$container.offset().left;
  }

  getBottom() {
    return this.getTop() + this.$container.height();
  }

  getRight() {
    return this.getLeft() + this.$container.width();
  }

  getRowTop(row, global) {
    const $row = this.$rows.find(`.row:eq(${row})`);
    const rowPosition = $row.position();
    let top = rowPosition.top;

    if (global) {
      top += this.getTop();
    }

    return top;
  }

  getRowBottom(row) {
    const $row = this.$rows.find(`.row:eq(${row})`);
    const rowPosition = $row.position();
    const bottom = rowPosition.top;

    return bottom + $row.height();
  }

  getColumnLeft(column) {
    const $row = this.$rows.find('.row:eq(0)');
    const $cell = $row.find(`.cell:eq(${column + 1})`);
    const cellPosition = $cell.position();
    const left = cellPosition ? cellPosition.left : 0;

    return left;
  }

  getColumnRight(column) {
    const $row = this.$rows.find('.row:eq(0)');
    const $cell = $row.find(`.cell:eq(${column + 1})`);
    const cellPosition = $cell.position();
    let right = cellPosition ? cellPosition.left : 0;

    right += $cell.width();

    return right;
  }

  // ---------------------------------------- OVERLAY ----------------------------------------

  onOverlayPressed() {
    this.closeHighlight();
    EventDispatcher.emit(GRID_ON_OVERLAY_PRESSED);
  }

  updateOverlaySize() {
    const gridSize = this.getSize();
    const mainWidth = this.$scrollContainer.get(0).clientWidth;

    const gridRight = Math.max(
      mainWidth,
      this.getColumnRight(gridSize.columns - 1),
    );

    this.$overlay.css({
      width: `${gridRight}px`,
    });
  }

  setOverlayRow(row) {
    const headerHeight = this.$header.height();
    const gridHeight = this.$container.height();

    const rowTop = this.getRowTop(row);
    const rowBottom = this.getRowBottom(row);

    const topHeight = rowTop - headerHeight;
    const bottomTop = rowBottom;
    const bottomHeight = gridHeight - bottomTop;

    const middleHeight = rowBottom - rowTop;

    const $top = this.$overlay.find('.top');
    const $bottom = this.$overlay.find('.bottom');
    const $middle = this.$overlay.find('.middle');

    $top.css({
      height: `${topHeight}px`,
    });

    $bottom.css({
      height: `${bottomHeight}px`,
    });

    $middle.css({
      height: `${middleHeight}px`,
    });
  }

  setOverlayColumn(column) {
    const gridWidth = this.$overlay.width();

    const columnLeft = this.getColumnLeft(column);
    const columnRight = this.getColumnRight(column);

    const leftWidth = columnLeft;
    const rightWidth = gridWidth - columnRight;

    const $middle = this.$overlay.find('.middle');
    const $left = $middle.find('.left');
    const $right = $middle.find('.right');

    $middle.show();

    $left.css({
      width: `${leftWidth}px`,
    });

    $right.css({
      width: `${rightWidth}px`,
    });
  }

  clearOverlayColumn() {
    this.$overlay.find('.middle').hide();
  }

  highlightCell(position) {
    this.setOverlayRow(position.row);
    this.setOverlayColumn(position.column);
    this.$overlay.addClass('active');
  }

  highlightRow(row) {
    this.setOverlayRow(row);
    this.clearOverlayColumn();
    this.$overlay.addClass('active');
  }

  closeHighlight() {
    document.removeEventListener('click', this.handleCellButtonsDocumentClick);
    this.$overlay.removeClass('active');
    this.$cellButtons.hide();
    this.$cellButtons.empty();
    Utils.callDelegate(this.delegate, 'onGridHighlightClosed');
  }

  // ---------------------------------------- CELL BUTTONS ----------------------------------------

  showCellButtons(position, buttons) {
    const $cellButtons = this.$cellButtons;

    $cellButtons.empty();

    const buttonsCenterX =
      (this.getColumnLeft(position.column) +
        this.getColumnRight(position.column)) /
      2;
    const buttonsTop = this.getRowBottom(position.row);

    $cellButtons.show();

    for (const button of buttons) {
      let content;

      if (button.icon) {
        content = Utils.sprite(button.icon);
      } else if (button.title) {
        content = button.title;
      }

      const $button = StickyTemplate.jq('grid-cell-button', {
        '.content': content,
      });

      if (button.extraClassNames) {
        $button.addClass(button.extraClassNames.join(' '));
      }

      $button.event(
        'click',
        function () {
          this.callback(this.position);
        }.bind({ callback: button.onPress, position: position }),
      );

      $cellButtons.append($button);
    }

    // Try center buttons under horse column center, but no further left than left edge (0)
    $cellButtons.css({
      left: `${Math.max(0, buttonsCenterX - $cellButtons.width() / 2)}px`,
      top: `${buttonsTop}px`,
    });

    document.addEventListener('click', this.handleCellButtonsDocumentClick);
  }

  handleCellButtonsDocumentClick(e) {
    const cellButtons = this.$cellButtons.get(0);

    const target = e.target;

    if (
      cellButtons &&
      target !== cellButtons &&
      !cellButtons.contains(target)
    ) {
      this.closeHighlight();
    }
  }

  // ---------------------------------------- INTERACTION METHODS ----------------------------------------

  showNumberInput(position, value) {
    const $row = this.$rows.find(`.row:eq(${position.row})`);
    const $cell = $row.find(
      `.cell:eq(${Number.parseInt(position.column + 1, 10)})`,
    );
    const $input = (this.$numberInput = $(
      `<input type="number" inputmode="numeric" min="0" max="255" value="${value}" data-row="${position.row}" data-column="${position.column}">`,
    ));
    $input.appendTo($cell.find('.bottom-blue'));

    this.$numberInput.on({
      focus: this.onInputFocus,
      keyup: this.onNumberInputChanged,
      change: this.onNumberInputChanged,
      keydown: this.onNumberInputKeyDown,
    });

    this.$numberInput.focus();
  }

  onInputFocus(e) {
    if (!isTouch()) {
      $(e.currentTarget).select();
    }
  }

  onNumberInputChanged() {
    Utils.callDelegate(
      this.delegate,
      'onGridNumberInputChanged',
      {
        row: this.$numberInput.data('row'),
        column: this.$numberInput.data('column'),
      },
      this.$numberInput.val(),
    );
  }

  onNumberInputKeyDown(e) {
    if (
      includes([46, 8, 27], e.keyCode) ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
    if (e.keyCode === 13) {
      this.closeHighlight();
      this.updateRow(this.$numberInput.data('row'));
    }
  }

  showLetterInput(position, value) {
    const $row = this.$rows.find(`.row:eq(${position.row})`);
    const $cell = $row.find(
      `.cell:eq(${Number.parseInt(position.column + 1, 10)})`,
    );
    const $input = (this.$letterInput = $(
      `<input type="text" style="text-transform: uppercase;" maxlength="1" value="${value}" data-row="${position.row}" data-column="${position.column}">`,
    ));
    $input.appendTo($cell.find('.bottom-blue'));

    this.$letterInput.on({
      focus: this.onInputFocus,
      keyup: this.onLetterInputChanged,
      change: this.onLetterInputChanged,
      keydown: this.onLetterInputKeyDown,
    });
    window.setTimeout(() => {
      this.$letterInput.focus();
    }, 50);
  }

  onLetterInputChanged() {
    const value = this.$letterInput.val();
    this.$letterInput.val(value.toUpperCase());
    Utils.callDelegate(
      this.delegate,
      'abcdReductionLetterChanged',
      {
        row: this.$letterInput.data('row'),
        column: this.$letterInput.data('column'),
      },
      this.$letterInput.val(),
    );
  }

  onLetterInputKeyDown(e) {
    if (
      includes([46, 8, 27], e.keyCode) ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }
    if (e.keyCode < 65 || e.keyCode > 72) {
      e.preventDefault();
    }
    if (e.keyCode === 13) {
      this.closeHighlight();
      this.updateRow(this.$letterInput.data('row'));
    }
  }
}
